import * as React from "react";
import Avatar from "@mui/material/Avatar";
import { Box, Divider, IconButton, Tooltip, tooltipClasses } from "@mui/material";
import { styled } from "@mui/material/styles";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import {
  MenuIcon,
  DarkMode as DarkModeOutlinedIcon,
  LightMode as LightModeOutlinedIcon,
} from "components/icons";

import "./Header.css";
import { BrandLogo } from "app/components/brand/BrandLogo";

import {
  getLoggedInUserFullName,
  getLoggedInUserInitials,
} from "app/services/app.state.service";
import { logout } from "app/services/auth.service";
import { USER_GUIDE_URL } from "AppConstants";
import { track } from "analytics/usageTracker";
import { FEATURE_USER_GUIDE } from "analytics/featureCatalog";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMaterialUIController, setMiniSidenav, setThemeMode } from "context";
import { AppearanceDrawer } from "app/components/appearance/AppearanceDrawer";

export const ProfileTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 500,
  },
});

export const Header = (props) => {
  let navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const logoutPendingRef = useRef(false);

  // Material UI Controller for sidenav toggle
  const [controller, sidenavDispatch] = useMaterialUIController();
  const { miniSidenav, darkMode } = controller;
  const themeSwitchLabel = darkMode ? "Switch to light theme" : "Switch to dark theme";

  function handleClick(event) {
    if (anchorEl !== event.currentTarget) {
      setAnchorEl(event.currentTarget);
    }
  }

  function handleClose() {
    setAnchorEl(null);
  }

  const handleToggleSidenav = () => {
    setMiniSidenav(sidenavDispatch, !miniSidenav);
  };

  const handleLogout = () => {
    logoutPendingRef.current = true;
    setAnchorEl(null);
  };

  const handleMenuExited = () => {
    if (logoutPendingRef.current) {
      logoutPendingRef.current = false;
      logout();
      window.location.href = "/login";
    }
  };

  if (!props.isLogin) {
    return (
      <div className="header-container">
        <Box sx={{ width: 160 }}>
          <BrandLogo />
        </Box>
      </div>
    );
  }

  const fullName = getLoggedInUserFullName();

  // if valid login load avatar
  return (
    <>
      <div className="header-container">
        <Box sx={{ display: { xs: 'flex', xl: 'none' }, alignItems: 'center' }}>
          <IconButton
            onClick={handleToggleSidenav}
            sx={{
              mr: 1,
              color: 'text.primary'
            }}
            aria-label="toggle navigation menu"
          >
            <MenuIcon />
          </IconButton>
        </Box>
        {/* The InOpTra HRMS logo takes the theme's colours, so it needs no dark-mode treatment. */}
        <Box sx={{ width: 160 }}>
          <BrandLogo />
        </Box>
        <div className="header-right">
          <Tooltip title={themeSwitchLabel}>
            <IconButton
              className="header-icon-button"
              aria-label={themeSwitchLabel}
              onClick={() => setThemeMode(sidenavDispatch, darkMode ? "light" : "dark")}
            >
              {darkMode ? <LightModeOutlinedIcon fontSize="small" /> : <DarkModeOutlinedIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Avatar
            className="avatar"
            sx={{ p: 2, bgcolor: "primary.main", color: "primary.contrastText" }}
            onClick={handleClick}
            onMouseOver={handleClick}
          >
            {getLoggedInUserInitials()}
          </Avatar>
        </div>
        <Menu
          id="simple-menu"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          // Closing the menu otherwise restores focus to whatever was focused
          // when it opened (e.g. the Apply/Generate button), and that .focus()
          // call scrolls the page back to that control. Skip the restore so the
          // page stays put.
          disableRestoreFocus
          slotProps={{
            list: { onMouseLeave: handleClose },
            transition: { onExited: handleMenuExited },
          }}
        >
          <MenuItem
            disabled
            sx={{
              "&.Mui-disabled": { opacity: 1, fontWeight: 600 },
              maxWidth: 280,
              whiteSpace: "normal",
            }}
          >
            <Box
              title={fullName}
              sx={{
                overflowWrap: "anywhere",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                lineHeight: 1.5,
                maxHeight: "3em",
              }}
            >
              Logged In User: {fullName}
            </Box>
          </MenuItem>
          <Divider
            sx={{
              backgroundImage: "none !important",
              backgroundColor: "var(--border) !important",
              opacity: 1,
              height: "1px",
              my: 1,
            }}
          />
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              setAppearanceOpen(true);
            }}
          >
            Appearance
          </MenuItem>
          <MenuItem
            component="a"
            href={USER_GUIDE_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              track(FEATURE_USER_GUIDE);
              handleClose();
            }}
          >
            User Guide
          </MenuItem>
          <MenuItem onClick={() => navigate("/change-password")}>Change Password</MenuItem>
          <MenuItem onClick={handleLogout}>Logout</MenuItem>
        </Menu>
      </div>
      <AppearanceDrawer open={appearanceOpen} onClose={() => setAppearanceOpen(false)} />
      {/* Open Menu */}
    </>
  );
};
